import { FC } from "react";
import { CameraKitSession, Lens } from "@snap/camera-kit";
import { Button, DropdownInput, Input, Option } from "@snapchat/snap-design-system";
import { MenuOnClickType } from "@snapchat/snap-design-system/lib/components/Menu";
import { CanvasContainer, FlexSection, Flexbox, inputStyle, transformMap } from "shared-resources";
import { controlContainerSmallDevice } from "../render-size/UseCase.styles";
import { mainStyling, submitContainer, submitStyling } from "./UseCase.styles";

interface TransformUseCaseProps {
    sessionOutput: CameraKitSession["output"];
    lenses: Lens[];
    applyTransform: ({ key }: MenuOnClickType) => Promise<void>;
    onLensSelect: (lens: Lens) => Promise<void>;
    customTransformInput: string;
    applyCustomTransform: () => Promise<void>;
    onCustomTransformChange: (value: string) => void;
    isValidTransform: boolean;
}

export const UseCase: FC<TransformUseCaseProps> = ({
    sessionOutput,
    lenses,
    applyTransform,
    onLensSelect,
    customTransformInput,
    applyCustomTransform,
    onCustomTransformChange,
    isValidTransform,
}) => {
    return (
        <Flexbox flexDirection="column" css={mainStyling}>
            <FlexSection
                // css={sectionDropdown}
                flexDirection="column"
                justifyContent="center"
            >
                <Flexbox>
                    <DropdownInput
                        disabled={!lenses.length}
                        placeholder="Choose a Lens..."
                        label="Lens"
                        onChange={(lensId) => {
                            const lens = lenses.find((lens) => lens.id === lensId);
                            if (lens) onLensSelect(lens);
                        }}
                    >
                        {lenses.map((lens: Lens) => (
                            <Option key={lens.id} value={lens.id}>
                                {lens.name}
                            </Option>
                        ))}
                    </DropdownInput>
                </Flexbox>
                <Flexbox css={controlContainerSmallDevice}>
                    <DropdownInput
                        label="Transform"
                        placeholder="Apply a Transform..."
                        onChange={(key) => {
                            applyTransform(transformMap[key].matrix);
                        }}
                        defaultValue={transformMap.mirrorX.label}
                        data-testid="transform-dropdown"
                    >
                        {Object.entries(transformMap).map(([mapKey, transformDetail]) => {
                            return (
                                <Option key={mapKey} value={mapKey} data-testid={transformDetail.label}>
                                    {transformDetail.label}
                                </Option>
                            );
                        })}
                    </DropdownInput>
                    <Input
                        css={inputStyle}
                        label="Custom Transform"
                        value={customTransformInput}
                        onChange={(key) => onCustomTransformChange(key)}
                        tooltip="3x3 matrix in the format [a, b, c, d, e, f, g, h, i]"
                        data-testid="custom-transform"
                    />
                    <div css={submitContainer}>
                        <Button
                            type="primary"
                            onClick={applyCustomTransform}
                            isDisabled={!isValidTransform}
                            data-testid="submit-button"
                            css={submitStyling}
                        >
                            Submit
                        </Button>
                    </div>
                </Flexbox>
            </FlexSection>
            <FlexSection flexDirection="row" flexWrap="wrap">
                <CanvasContainer canvasOutput={sessionOutput.live} />
            </FlexSection>
        </Flexbox>
    );
};
